import { useState, useEffect } from 'react'
import { Trophy } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useLeaderboard } from '@/hooks/useFriends'
import { format } from 'date-fns'
import { DateNavigation } from './DateNavigation'
import { GameTabs } from './GameTabs'
import { LeaderboardDisplay } from './LeaderboardDisplay'

interface FriendsLeaderboardProps {
  className?: string
  currentUserId?: string
}


export function FriendsLeaderboard({ className, currentUserId }: FriendsLeaderboardProps) {
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [selectedGameId, setSelectedGameId] = useState('all')
  const [isNavigating, setIsNavigating] = useState(false)


  // Format date for API call
  const dateString = format(selectedDate, 'yyyy-MM-dd')
  
  // Use the leaderboard hook
  const { data: leaderboardData, isLoading, error, refetch } = useLeaderboard(dateString, selectedGameId)

  // Handle date navigation
  const handleDateChange = (newDate: Date) => {
    if (isNavigating) return
    setIsNavigating(true)
    setSelectedDate(newDate)
    setTimeout(() => setIsNavigating(false), 300)
  }

  // Handle game tab change
  const handleGameChange = (gameId: string) => {
    setSelectedGameId(gameId)
  }

  // Handle retry
  const handleRetry = () => {
    refetch()
  }

  const isLoadingOrNavigating = isLoading || isNavigating

  return (
    <section className={cn("bg-white dark:bg-gray-800 rounded-2xl p-8 max-sm:p-4 shadow-sm", className)}>
      {/* Section Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-bebas-neue text-4xl tracking-[2px] uppercase text-foreground mb-2">
              Friends Leaderboard
            </h2>
            <p className="font-oswald text-lg font-normal text-muted-foreground">
              See how you stack up against your friends
            </p>
          </div>
          <Trophy className="h-12 w-12 text-amber-500" />
        </div>

        {/* Date Navigation */}
        <DateNavigation 
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
          disabled={isNavigating}
          className="mb-6"
        />

        {/* Game Tabs */}
        <GameTabs 
          selectedGameId={selectedGameId}
          onGameChange={handleGameChange}
          disabled={isNavigating}
        />
      </div>

      {/* Leaderboard Content */}
      <LeaderboardDisplay 
        data={leaderboardData}
        isLoading={isLoadingOrNavigating}
        error={error}
        onRetry={handleRetry}
      />
    </section>
  )
}

export default FriendsLeaderboard